import React from 'react';
import { Box, Button, CircularProgress } from '@mui/material';

type ModalPhase = 'idle' | 'evaluating' | 'evaluated';

interface UploadModalFooterProps {
  phase: ModalPhase;
  hasFiles: boolean;
  allValid: boolean;
  hasChangedSinceEvaluation: boolean;
  onEvaluate: () => void;
  onReEvaluate: () => void;
  onUpdate: () => void;
  updatingTechLandscape: boolean;
}

const UploadModalFooter: React.FC<UploadModalFooterProps> = ({
  phase,
  hasFiles,
  allValid,
  hasChangedSinceEvaluation,
  onEvaluate,
  onReEvaluate,
  onUpdate,
  updatingTechLandscape,
}) => {
  const evaluateButtonSx = {
    textTransform: 'none',
    fontFamily: 'Poppins, sans-serif',
    fontSize: 18,
    fontWeight: 400,
    color: '#CE7900',
    backgroundColor: '#fff9f1ff',
    borderColor: '#DB9B43',
    borderRadius: '8px',
    px: 3,
    py: 1,
    '&:hover': { backgroundColor: '#f5f0e8ff' },
    '&.Mui-disabled': { opacity: 0.5 },
  };

  const sparkleIcon = (
    <img src="/images/yellow-sparkle.svg" alt="evaluate" width={18} height={18} />
  );

  return (
    <Box
      sx={{
        px: 3,
        py: 2,
        borderTop: '1px solid #E8EAED',
        display: 'flex',
        justifyContent: 'flex-end',
        gap: 1.5,
        backgroundColor: '#FFFFFF',
      }}
    >
      {/* ── Re-evaluate: visible only when evaluated + files changed ── */}
      {phase === 'evaluated' && hasChangedSinceEvaluation && (
        <Button
          variant="outlined"
          onClick={onReEvaluate}
          startIcon={sparkleIcon}
          sx={evaluateButtonSx}
        >
          Re-evaluate
        </Button>
      )}

      {/* ── Evaluate: visible in idle / evaluating phases ── */}
      {phase !== 'evaluated' && (
        <Button
          variant="outlined"
          disabled={!hasFiles || phase === 'evaluating'}
          onClick={onEvaluate}
          startIcon={
            phase === 'evaluating' ? (
              <CircularProgress size={14} sx={{ color: '#CE7900' }} />
            ) : (
              sparkleIcon
            )
          }
          sx={evaluateButtonSx}
        >
          {phase === 'evaluating' ? 'Evaluating…' : 'Evaluate'}
        </Button>
      )}

      {/* ── Update: visible after evaluation, disabled unless all valid ── */}
      {phase === 'evaluated' && (
        <Button
          variant="contained"
          onClick={onUpdate}
          disabled={!allValid || updatingTechLandscape}
          sx={{
            textTransform: 'none',
            fontFamily: 'Poppins, sans-serif',
            fontSize: 18,
            fontWeight: 400,
            backgroundColor: '#1A55F9',
            borderRadius: '8px',
            px: '12px',
            py: '12px',
            '&:hover': { backgroundColor: '#1444d0' },
            '&.Mui-disabled': {
              backgroundColor: '#A0B4FB',
              color: '#fff',
              cursor: 'not-allowed',
            },
          }}
          startIcon={
            updatingTechLandscape ? <CircularProgress size={14} sx={{ color: '#fff' }} /> : ''
          }
        >
          {updatingTechLandscape ? 'Updating' : 'Update Tech Landscape'}
        </Button>
      )}
    </Box>
  );
};

export default UploadModalFooter;
